import { Action, Select, Selector, State, StateContext } from "@ngxs/store";
import { Withdrawal, WithdrawalData } from "../model/withdrawal.model";
import { Injectable } from "@angular/core";
import {
  ClearWithdrawal,
  GetDonateWithdrawals,
  GetStoreWithdrawals,
  ReloadWithdrawals} from "./withdrawal.action";
import {
  catchError,
  filter,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap} from "rxjs";
import { WithdrawalService } from "../service/withdrawal.service";
import { fromWithdrawalResponseDtoToWithdrawal } from "../mapper/withdrawal.mapper";
import { WalletState } from "../../wallet/state/wallet.state";
import { Wallet } from "../../wallet/model/wallet.model";

export class WithdrawalStateModel {
  donate: Withdrawal;
  store: Withdrawal;
  donateError: Error;
  storeError: Error;
  isDonateLoading: boolean;
  isStoreLoading: boolean;
}

@State<WithdrawalStateModel>({
  name: "withdrawal",
  defaults: {
    donate: undefined!,
    store: undefined!,
    donateError: undefined!,
    storeError: undefined!,
    isDonateLoading: true,
    isStoreLoading: true}})
@Injectable()
export class WithdrawalState {
  @Select(WalletState.wallets) wallets$: Observable<Wallet[]>;

  constructor(private _withdrawalService: WithdrawalService) {}

  @Selector()
  static donateWithdrawalPage(state: WithdrawalStateModel): {
    [page: number]: WithdrawalData[];
  } {
    return state.donate?.data ?? [];
  }

  @Selector()
  static donateWithdrawalPagination(state: WithdrawalStateModel) {
    return state.donate?.pagination;
  }

  @Selector()
  static storeWithdrawalPage(state: WithdrawalStateModel): {
    [page: number]: WithdrawalData[];
  } {
    return state.store?.data ?? [];
  }

  @Selector()
  static storeWithdrawalPagination(state: WithdrawalStateModel) {
    return state.store?.pagination;
  }

  @Selector()
  static isDonateLoading(state: WithdrawalStateModel): boolean {
    return state.isDonateLoading;
  }

  @Selector()
  static hasDonateError(state: WithdrawalStateModel): boolean {
    return !!state.donateError;
  }

  @Selector()
  static isStoreLoading(state: WithdrawalStateModel): boolean {
    return state.isStoreLoading;
  }

  @Selector()
  static hasStoreError(state: WithdrawalStateModel): boolean {
    return !!state.storeError;
  }

  @Action(GetDonateWithdrawals)
  getDonateWithdrawals(
    ctx: StateContext<WithdrawalStateModel>,
    action: GetDonateWithdrawals,
  ) {
    const state = ctx.getState();
    const withdrawal = state.donate;

    const alreadyLoaded =
      withdrawal && withdrawal.data && withdrawal.data[action.page];

    if (alreadyLoaded) {
      return of(withdrawal);
    }

    ctx.patchState({
      isDonateLoading: true});

    return this.wallets$.pipe(
      filter((wallets) => Array.isArray(wallets) && wallets.length > 0),
      take(1),
      map((wallets) => wallets.find((wallet) => wallet.type === "DONATE")),
      switchMap((donateWallet) => {
        if (!donateWallet) return of(undefined);
        return this._withdrawalService
          .getWithdrawal(donateWallet.id, action.page, action.pageSize)
          .pipe(
            tap((response) => {
              const newWithdrawal =
                fromWithdrawalResponseDtoToWithdrawal(response);

              const currentData =
                withdrawal &&
                typeof withdrawal.data === "object" &&
                !Array.isArray(withdrawal.data)
                  ? withdrawal.data
                  : {};

              ctx.patchState({
                donate: {
                  data: {
                    ...currentData,
                    [action.page]: newWithdrawal.data[action.page]},
                  pagination: newWithdrawal.pagination},
                donateError: undefined!,
                isDonateLoading: false});
            }),
          );
      }),
      catchError((error) => {
        ctx.patchState({
          donateError: error,
          isDonateLoading: false});
        return of();
      }),
    );
  }

  @Action(GetStoreWithdrawals)
  getStoreWithdrawals(
    ctx: StateContext<WithdrawalStateModel>,
    action: GetStoreWithdrawals,
  ) {
    const state = ctx.getState();
    const withdrawal = state.store;

    const alreadyLoaded =
      withdrawal && withdrawal.data && withdrawal.data[action.page];

    if (alreadyLoaded) {
      return of(withdrawal);
    }

    ctx.patchState({
      isStoreLoading: true});

    return this.wallets$.pipe(
      filter((wallets) => Array.isArray(wallets) && wallets.length > 0),
      take(1),
      map((wallets) => wallets.find((wallet) => wallet.type === "MARKET")),
      switchMap((storeWallet) => {
        if (!storeWallet) return of(undefined);
        return this._withdrawalService
          .getWithdrawal(storeWallet.id, action.page, action.pageSize)
          .pipe(
            tap((response) => {
              const newWithdrawal =
                fromWithdrawalResponseDtoToWithdrawal(response);

              const currentData =
                withdrawal &&
                typeof withdrawal.data === "object" &&
                !Array.isArray(withdrawal.data)
                  ? withdrawal.data
                  : {};

              ctx.patchState({
                store: {
                  data: {
                    ...currentData,
                    [action.page]: newWithdrawal.data[action.page]},
                  pagination: newWithdrawal.pagination},
                storeError: undefined!,
                isStoreLoading: false});
            }),
          );
      }),
      catchError((error) => {
        ctx.patchState({
          storeError: error,
          isStoreLoading: false});
        return of();
      }),
    );
  }

  @Action(ReloadWithdrawals)
  reloadWithdrawals(
    ctx: StateContext<WithdrawalStateModel>,
    action: ReloadWithdrawals,
  ) {
    ctx.patchState({
      donate: undefined!,
      store: undefined!,
      donateError: undefined!,
      storeError: undefined!,
      isDonateLoading: true,
      isStoreLoading: true});

    ctx.dispatch(new GetDonateWithdrawals(action.page, action.pageSize));
    ctx.dispatch(new GetStoreWithdrawals(action.page, action.pageSize));

    return of();
  }

  @Action(ClearWithdrawal)
  clearWithdrawal(ctx: StateContext<WithdrawalStateModel>) {
    ctx.patchState({
      donate: undefined!,
      store: undefined!,
      donateError: undefined!,
      storeError: undefined!,
      isDonateLoading: true,
      isStoreLoading: true});
    return of();
  }
}
